import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { useDebounce } from "use-debounce"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Textarea } from "~/ui/textarea"
import { Form } from "~/ui/form"
import { TagField, TagFieldTag } from "~/fields/tag-field"
import { CONTACT_UPDATE_MUTATION } from "~/contacts/contact-update-dialog"
import { ContactProfileFragment } from "~/__generated__/graphql"
import Text from "~/ui/typography"

interface ContactNotesSectionProps {
  contact: ContactProfileFragment
}

interface FormData {
  tagInput: string
  notes: string
}

export function ContactTagsAndNotesSection({ contact }: ContactNotesSectionProps) {
  const [notes, setNotes] = useState(contact.notes || "")
  const [selectedTags, setSelectedTags] = useState<TagFieldTag[]>(
    contact.tags?.map((tag) => ({ id: tag.id, name: tag.name })) || []
  )
  const [debouncedNotes] = useDebounce(notes, 1000) // 1 second debounce
  const [debouncedTags] = useDebounce(selectedTags, 1000) // 1 second debounce

  const form = useForm<FormData>({
    defaultValues: {
      tagInput: "",
      notes: contact.notes || "",
    },
  })

  const [updateContact] = useSafeMutation(CONTACT_UPDATE_MUTATION)

  // Auto-save when debounced notes change and differ from original
  useEffect(() => {
    const originalNotes = contact.notes || ""
    const currentNotes = debouncedNotes.trim()

    if (currentNotes !== originalNotes) {
      updateContact({
        variables: {
          input: {
            id: contact.id,
            contactInput: {
              notes: currentNotes || null,
            },
          },
        },
      })
    }
  }, [debouncedNotes, contact.notes, contact.id, updateContact])

  // Auto-save when debounced tags change
  useEffect(() => {
    const originalTags = contact.tags?.map((tag) => ({ id: tag.id, name: tag.name })) || []
    const currentTags = debouncedTags

    // Check if tags have changed
    const tagsChanged =
      originalTags.length !== currentTags.length ||
      originalTags.some((tag) => !currentTags.find((t) => t.id === tag.id)) ||
      currentTags.some((tag) => !originalTags.find((t) => t.id === tag.id))

    if (tagsChanged) {
      const existingTagIds = currentTags.filter((tag) => !tag.isNew).map((tag) => tag.id)
      const newTags = currentTags.filter((tag) => tag.isNew).map((tag) => tag.name)

      updateContact({
        variables: {
          input: {
            id: contact.id,
            contactInput: {
              tagIds: existingTagIds,
              newTags: newTags,
            },
          },
        },
      })
    }
  }, [debouncedTags, contact.tags, contact.id, updateContact])

  return (
    <div className="space-y-6">
      <Text variant="heading4" as="h3">
        Tags
      </Text>
      <hr />

      <Form {...form}>
        <div className="space-y-4">
          {/* Tags Field */}
          <TagField
            control={form.control}
            name="tagInput"
            label="Tags"
            placeholder="Add tags to organize this contact..."
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            hideLabel={true}
          />

          {/* Notes Field */}
          <div className="space-y-2">
            <Text variant="subheader_bold">Internal Notes</Text>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add private notes about this contact..."
              rows={4}
              className="resize-none"
            />
          </div>
        </div>
      </Form>
    </div>
  )
}
